import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import * as z from "zod";
import { useApi } from "@/hooks/useApi";
import { pollWhileSyncing, useSnapshotSectionTiming } from "@/hooks/githubSnapshot";
import { type NamedOrg, namedOrgKey, orgApiPath } from "@/lib/named-org";
const RepoPermissionsSnapshot = z.object({
  synced_at: z.string().nullable(),
  attempted_at: z.string().nullable(),
  failure: z.string().nullable(),
  row_count: z.number()
});
const QUERY_KEY = "repo-permissions-snapshot";
const toastId = (orgKey: string) => `repo-permissions-sync-${orgKey}`;

/**
 * Keeps the page honest while the user's repository permissions for `org` are still being written by their first
 * sync. Listings answer the rows landed so far, so this polls the snapshot, refetches the page's queries as more rows
 * land, and shows a closable toast until the sync lands or gives up.
 */
export function useRepoPermissionsSync(org) {
  const $ = _c(28);
  const api = useApi();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== org) {
    t0 = namedOrgKey(org);
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const orgKey = t0;
  let t1;
  if ($[2] !== orgKey) {
    t1 = [QUERY_KEY, orgKey];
    $[2] = orgKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== api || $[5] !== org) {
    t2 = async () => {
      const response = await api.get(`${orgApiPath(org)}/repository-permissions/snapshot`);
      return {
        snapshot: RepoPermissionsSnapshot.parse(response.data)
      };
    };
    $[4] = api;
    $[5] = org;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = org !== null;
  let t4;
  if ($[7] !== t3) {
    t4 = {
      enabled: t3,
      staleTime: Number.POSITIVE_INFINITY,
      refetchInterval: _temp
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: data_0,
    status
  } = useQuery(t1, t2, t4);
  useSnapshotSectionTiming("repo_permissions.snapshot", status, data_0?.snapshot ?? null);
  const progress = data_0 ? `${data_0.snapshot.row_count}:${data_0.snapshot.synced_at}` : undefined;
  const seen = useRef(null);
  let t5;
  let t6;
  if ($[9] !== orgKey || $[10] !== progress || $[11] !== queryClient) {
    t5 = () => {
      if (progress === undefined) {
        return;
      }
      const previous = seen.current;
      seen.current = {
        orgKey,
        progress
      };
      if (previous !== null && previous.orgKey === orgKey && previous.progress !== progress) {
        queryClient.invalidateQueries({
          predicate: _temp2
        });
      }
    };
    t6 = [orgKey, progress, queryClient];
    $[9] = orgKey;
    $[10] = progress;
    $[11] = queryClient;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== data_0 || $[15] !== org) {
    t7 = org !== null && pollWhileSyncing(data_0) !== false;
    $[14] = data_0;
    $[15] = org;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const syncing = t7;
  const synced = data_0?.snapshot.synced_at !== null;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = new Set();
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const closed = useRef(t8);
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = new Set();
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const shown = useRef(t9);
  let t10;
  let t11;
  if ($[19] !== org || $[20] !== orgKey || $[21] !== synced || $[22] !== syncing) {
    t10 = () => {
      if (org === null || orgKey === undefined) {
        return;
      }
      const id = toastId(orgKey);
      if (syncing) {
        if (closed.current.has(orgKey)) {
          return;
        }
        shown.current.add(orgKey);
        toast(`Syncing ${org.orgName} repositories`, {
          id,
          description: "Some data may be incomplete until this finishes.",
          duration: Number.POSITIVE_INFINITY,
          onDismiss: () => closed.current.add(orgKey)
        });
        return;
      }
      if (!shown.current.delete(orgKey)) {
        return;
      }
      if (synced) {
        toast.success(`${org.orgName} repositories synced`, {
          id
        });
      } else {
        toast.dismiss(id);
      }
    };
    t11 = [org, orgKey, syncing, synced];
    $[19] = org;
    $[20] = orgKey;
    $[21] = synced;
    $[22] = syncing;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[25] !== orgKey) {
    t12 = () => {
      if (orgKey === undefined) {
        return;
      }
      const shownOrgs = shown.current;
      return () => {
        if (shownOrgs.delete(orgKey)) {
          toast.dismiss(toastId(orgKey));
        }
      };
    };
    t13 = [orgKey];
    $[25] = orgKey;
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  useEffect(t12, t13);
}
function _temp2(query) {
  return query.queryKey[0] !== QUERY_KEY;
}
function _temp(data) {
  return pollWhileSyncing(data);
}